import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { CreateRestaurantRequest } from '@/bindings/CreateRestaurantRequest'
import type { Language } from '@/bindings/Language'
import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import { Button } from '@/components/button'
import { Checkbox, Field, Input, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { restaurantKeys } from '@/features/restaurants/api'
import { emptyBranchDraft, toBranchRequest } from '@/features/restaurants/branch-draft'
import { BranchFields } from '@/features/restaurants/branch-fields'
import { anyIdentifierTyped, emptyIdentifierDraft } from '@/features/auth/identifier-draft'
import { SlugField } from '@/features/restaurants/slug-field'
import { type SuperuserPick, SuperuserPicker } from '@/features/restaurants/superuser-picker'
import { useSlugVerdict } from '@/features/restaurants/use-slug-verdict'
import { DEFAULT_ACCENT } from '@/lib/accent'
import { ApiError, claim, request } from '@/lib/api'
import { LANGUAGES, LANGUAGE_NAMES } from '@/lib/i18n/languages'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { slugify } from '@/lib/slug'

/**
 * Platform create form (PRD): restaurant + its first branch, plus an optional
 * superuser link by exact phone or email, looked up before submit (E2 — a
 * restaurant may start superuser-less).
 */
export function CreateRestaurantPage() {
  const t = useT()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const uiLanguage = useLanguage()

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [color, setColor] = useState(DEFAULT_ACCENT)
  const [mainLanguage, setMainLanguage] = useState<Language>(uiLanguage)
  const [secondary, setSecondary] = useState<readonly Language[]>([])
  const [branch, setBranch] = useState(emptyBranchDraft)
  const [superuserDraft, setSuperuserDraft] = useState(() => emptyIdentifierDraft())
  const [pick, setPick] = useState<SuperuserPick>()
  const [searchFirst, setSearchFirst] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const slugVerdict = useSlugVerdict(slug, { remote: true })
  // A new restaurant has no siblings: the branch slug only needs to be well-formed.
  const branchSlugVerdict = useSlugVerdict(branch.slug)

  const create = useMutation({
    mutationFn: () => {
      const body: CreateRestaurantRequest = {
        slug,
        name: name.trim(),
        color,
        main_language: mainLanguage,
        secondary_languages: secondary.filter((lang) => lang !== mainLanguage),
        branch: toBranchRequest(branch),
        superuser: pick?.identifier ?? null,
      }
      return request<RestaurantDetail>('/platform/restaurants', { method: 'POST', body })
    },
    onSuccess: (restaurant) => {
      queryClient.setQueryData(restaurantKeys.detail(restaurant.id), restaurant)
      void queryClient.invalidateQueries({ queryKey: restaurantKeys.all })
      return navigate({ to: '/restaurants/$restaurantId', params: { restaurantId: restaurant.id }, replace: true })
    },
    // The link re-resolves the identifier: an account gone since the lookup drops the pick.
    onError: (error) => {
      if (claim(error, 'ACCOUNT_NOT_FOUND')) setPick(undefined)
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    setSubmitted(true)
    // Typed (on either channel) but never searched: the owner must be confirmed to exist first.
    const unsearched = !pick && anyIdentifierTyped(superuserDraft)
    setSearchFirst(unsearched)
    if (!slugVerdict.ok || !branchSlugVerdict.ok || unsearched) return
    create.mutate()
  }

  function chooseMain(lang: Language) {
    setMainLanguage(lang)
    setSecondary(secondary.filter((other) => other !== lang))
  }

  function toggleSecondary(lang: Language, on: boolean) {
    setSecondary(on ? [...secondary, lang] : secondary.filter((other) => other !== lang))
  }

  // The restaurant row is inserted first, so a slug verdict at create is the restaurant's (E2).
  const slugError = create.error instanceof ApiError && create.error.code.startsWith('SLUG_') ? create.error : undefined
  const formError = slugError ? undefined : create.error

  return (
    <Page width="md" title={t('restaurants.create')}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('restaurants.restaurantSection')}</h2>
          <Field label={t('restaurants.name')}>
            <Input
              required
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (!slugEdited) setSlug(slugify(e.target.value))
              }}
            />
          </Field>
          <SlugField
            label={t('restaurants.slug')}
            value={slug}
            onChange={(next) => {
              setSlug(next)
              setSlugEdited(true)
              create.reset()
            }}
            verdict={slugVerdict}
            submitted={submitted}
            error={slugError}
            hint={t('restaurants.slugHint')}
          />
          <Field label={t('restaurants.color')}>
            <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-11 w-24 p-1" />
          </Field>
          <Field label={t('restaurants.mainLanguage')}>
            <Select value={mainLanguage} onChange={(e) => chooseMain(e.target.value as Language)}>
              {LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {LANGUAGE_NAMES[lang]}
                </option>
              ))}
            </Select>
          </Field>
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-sm font-medium">{t('restaurants.secondaryLanguages')}</legend>
            {LANGUAGES.filter((lang) => lang !== mainLanguage).map((lang) => (
              <Checkbox
                key={lang}
                label={LANGUAGE_NAMES[lang]}
                checked={secondary.includes(lang)}
                onChange={(e) => toggleSecondary(lang, e.target.checked)}
              />
            ))}
          </fieldset>
        </Card>

        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('restaurants.firstBranch')}</h2>
          <BranchFields value={branch} onChange={setBranch} slugVerdict={branchSlugVerdict} submitted={submitted} />
        </Card>

        <Card className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">
            {t('restaurants.superuser')} ({t('common.optional')})
          </h2>
          <p className="text-muted text-sm">{t('restaurants.superuserHint')}</p>
          <SuperuserPicker
            draft={superuserDraft}
            onDraftChange={(next) => {
              setSuperuserDraft(next)
              setSearchFirst(false)
              if (claim(create.error, 'ACCOUNT_NOT_FOUND')) create.reset()
            }}
            pick={pick}
            onPick={(next) => {
              setPick(next)
              setSearchFirst(false)
            }}
            searchFirst={searchFirst}
          />
        </Card>

        <FormError error={formError} />
        <div className="flex gap-2">
          <Button type="submit" loading={create.isPending} disabled={slugVerdict.availability === 'checking'}>
            {t('restaurants.create')}
          </Button>
          <Link to="/restaurants" search={{ page: 1 }} className="text-accent-ink hover:bg-accent-soft flex min-h-tap items-center rounded-lg px-4 font-medium">
            {t('common.cancel')}
          </Link>
        </div>
      </form>
    </Page>
  )
}
